import React, { useEffect, useRef, useState } from 'react';
import { TextField } from "@mui/material";
import { FormSearchAdornmentGray } from "@securspace/securspace-ui-kit";

/**
 * Long enough to coalesce a burst of typing into one query update, short enough
 * that the results still feel live. Below ~250ms the report refetches mid-word;
 * above ~600ms the filter reads as laggy.
 */
const DEFAULT_DEBOUNCE_MS = 400;

type BoundTextInputProps<T> = {
  name: string;
  value?: string | number | null;
  label: string;
  /**
   * Digits only. Use for params the API binds to a numeric type — e.g.
   * `invoiceNumber` maps to a `java.lang.Long`, so a stray letter comes back as
   * a NumberFormatException toast instead of an empty result set.
   */
  numeric?: boolean;
  /** Override the commit delay; 0 commits synchronously. */
  debounceMs?: number;
  onChange: (newQuery: T) => void;
}

/**
 * Text filter bound to a report query.
 *
 * The field owns its text while you type and only pushes to `onChange` once
 * typing pauses. Without that, every keystroke rewrote the URL query, refetched
 * the report and swapped the rows for skeletons — a flicker per character.
 */
const BoundTextInput = <T,>({
  name,
  value = '',
  label,
  numeric = false,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  onChange
}: BoundTextInputProps<T>) => {
  const [text, setText] = useState(String(value ?? ''));
  // What we last handed to `onChange`, i.e. what the bound query should echo
  // back. Lets us tell a genuine outside change from our own round-trip.
  const committed = useRef(String(value ?? ''));
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // A pending commit fires from a closure captured when the key was pressed.
  // Parent handlers merge into the query they saw at that point, so calling a
  // stale one would write a since-changed sibling filter back to its old value.
  const latestOnChange = useRef(onChange);
  useEffect(() => { latestOnChange.current = onChange; });

  // Outside resets (URL edit, clear filters, back button) win — but only when
  // they differ from what we sent, so a late echo can't clobber fresher typing.
  useEffect(() => {
    const next = String(value ?? '');
    if (next !== committed.current) {
      // Drop any queued commit: an outside reset must not be undone a moment
      // later by the keystroke that was still waiting out its delay.
      clearTimeout(timer.current);
      committed.current = next;
      setText(next);
    }
  }, [value]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const commit = (next: string) => {
    committed.current = next;
    latestOnChange.current({ [name]: next } as unknown as T);
  }

  const onTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event?.target?.value ?? '';
    const next = numeric ? raw.replace(/\D/g, '') : raw;
    setText(next);

    clearTimeout(timer.current);
    if (debounceMs > 0) {
      timer.current = setTimeout(() => commit(next), debounceMs);
    } else {
      commit(next);
    }
  }

  // Don't make someone wait out the delay when they've clearly finished.
  const flush = () => {
    clearTimeout(timer.current);
    if (text !== committed.current) {
      commit(text);
    }
  }

  return <TextField
    label={label}
    name={name}
    value={text}
    onChange={onTextChange}
    onBlur={flush}
    onKeyDown={(event) => { if (event.key === 'Enter') flush(); }}
    /* below this are standard display properties for MUI TextField */
    variant={'standard'}
    fullWidth={true}
    InputProps={{
      endAdornment: <FormSearchAdornmentGray />
    }}
    inputProps={numeric ? { inputMode: 'numeric' } : undefined}
  />
}

export { BoundTextInput };
